import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "../context/AuthContext";
import { 
  Rocket, 
  Search, 
  Brain, 
  BarChart3, 
  Shield, 
  Zap,
  ChevronRight,
  Sparkles,
  User,
  Lock,
  LogIn,
  UserPlus,
  Loader2
} from "lucide-react";

const features = [
  {
    icon: Search,
    title: "Smart Job Discovery",
    description: "AI-powered scraping from multiple sources including JSearch, USAJobs, and RemoteOK"
  },
  {
    icon: Brain,
    title: "Intelligent Scoring",
    description: "Automatic lead scoring based on your skills profile - AWS, DevSecOps, AI, GovCloud"
  },
  {
    icon: BarChart3,
    title: "Pipeline Management",
    description: "Kanban-style tracking from New to Offer with drag-and-drop simplicity"
  },
  {
    icon: Shield,
    title: "Link Verification",
    description: "AI agents verify job postings are still active and accessible"
  },
  {
    icon: Zap,
    title: "Action Automation",
    description: "Auto-generate emails, LinkedIn DMs, and resume suggestions for each lead"
  },
  {
    icon: Sparkles,
    title: "Deduplication",
    description: "Fuzzy matching prevents duplicate leads and keeps your pipeline clean"
  }
];

export default function SplashPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle, pendingApproval } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [showPendingMessage, setShowPendingMessage] = useState(pendingApproval);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setShowPendingMessage(false);
    
    if (!email.trim() || !password.trim()) {
      setError("Please enter email and password");
      return;
    }

    if (mode === "register" && !name.trim()) {
      setError("Please enter your name");
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else {
        const result = await register(email, password, name);
        if (result.pending) {
          setShowPendingMessage(true);
        } else {
          navigate("/");
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Authentication failed";
      if (message.includes("pending")) {
        setShowPendingMessage(true);
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    loginWithGoogle();
  };

  // Show pending approval message
  if (showPendingMessage) {
    return (
      <div className="min-h-screen bg-swirl flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="p-8 rounded-xl bg-[#1a3a42]/90 border-2 border-[#e9c46a]/40 backdrop-blur-sm"
            style={{ boxShadow: "6px 6px 0 rgba(38, 70, 83, 0.5)" }}>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#e9c46a]/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#e9c46a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[#e9c46a] mb-2">Pending Approval</h2>
            <p className="text-[#a8c8c4] mb-6">
              Your account has been created and is pending admin approval. 
              You'll be able to sign in once an administrator approves your account.
            </p>
            <button
              onClick={() => setShowPendingMessage(false)}
              className="px-6 py-2 bg-[#2a9d8f] hover:bg-[#40c9ba] text-white rounded-lg transition-all"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen vg-swirl flex flex-col"
      data-testid="splash-page"
    >
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Logo & Title */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div 
              className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-[#e9c46a] mx-auto"
              style={{ 
                boxShadow: "8px 8px 0 rgba(139, 69, 19, 0.5), 0 0 40px rgba(233, 196, 106, 0.3)" 
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_gov-deal-hunt/artifacts/qbft4dvs_hero.png" 
                alt="Westbay Contract Hunter"
                className="w-full h-full object-cover"
              />
            </div>
            <div 
              className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-[#2a9d8f] flex items-center justify-center border-2 border-[#e9c46a]"
              style={{ boxShadow: "3px 3px 0 rgba(42, 157, 143, 0.4)" }}
            >
              <Rocket className="w-5 h-5 text-[#faf3e0]" />
            </div>
          </div>
          
          <h1 
            className="font-spraypaint text-5xl md:text-7xl text-[#e9c46a] mb-4"
            style={{ textShadow: "4px 4px 8px rgba(0,0,0,0.5)" }}
          >
            Westbay
          </h1>
          <p 
            className="text-xl md:text-2xl text-[#f4a261] font-bold tracking-wider"
            style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.4)" }}
          >
            CONTRACT HUNTER
          </p>
          
          <p className="mt-6 text-[#a8c8c4] text-lg max-w-xl mx-auto font-mono">
            Your AI-powered companion for finding and managing government & commercial contracts
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm mx-auto">
          <div 
            className="p-6 rounded-xl bg-[#1a3a42]/90 border-2 border-[#2a9d8f]/40 backdrop-blur-sm"
            style={{ boxShadow: "6px 6px 0 rgba(38, 70, 83, 0.5)" }}
          >
            {/* Mode Toggle */}
            <div className="flex mb-6 bg-[#264653] rounded-lg p-1">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === "login"
                    ? "bg-[#2a9d8f] text-white"
                    : "text-[#a8c8c4] hover:text-white"
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === "register"
                    ? "bg-[#2a9d8f] text-white"
                    : "text-[#a8c8c4] hover:text-white"
                }`}
              >
                Register
              </button>
            </div>

            <div className="space-y-4">
              {/* Name Field (Register only) */}
              {mode === "register" && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#a8c8c4] text-sm font-medium">
                    Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2a9d8f]" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] placeholder:text-[#a8c8c4]/50 focus:border-[#e9c46a] focus:ring-[#e9c46a]/20"
                      data-testid="register-name"
                    />
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#a8c8c4] text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2a9d8f]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] placeholder:text-[#a8c8c4]/50 focus:border-[#e9c46a] focus:ring-[#e9c46a]/20"
                    data-testid="login-email"
                  />
                </div>
              </div>
              
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#a8c8c4] text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2a9d8f]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-[#264653] border-[#2a9d8f]/50 text-[#faf3e0] placeholder:text-[#a8c8c4]/50 focus:border-[#e9c46a] focus:ring-[#e9c46a]/20"
                    data-testid="login-password"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <p className="text-[#e76f51] text-sm text-center">{error}</p>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full py-5 text-lg font-bold bg-gradient-to-r from-[#e9c46a] to-[#f4a261] hover:from-[#f4a261] hover:to-[#e76f51] text-[#264653] rounded-lg transition-all duration-300"
                style={{ boxShadow: "4px 4px 0 rgba(139, 69, 19, 0.4)" }}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {mode === "login" ? "Signing in..." : "Creating account..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {mode === "login" ? <LogIn className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    {mode === "login" ? "Sign In" : "Create Account"}
                  </span>
                )}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#2a9d8f]/30"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-[#1a3a42] text-[#a8c8c4]">or</span>
                </div>
              </div>

              {/* Google Login */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                variant="outline"
                className="w-full py-5 text-base font-medium border-2 border-[#2a9d8f]/50 text-[#faf3e0] hover:bg-[#2a9d8f]/20 rounded-lg transition-all duration-300"
                data-testid="google-login-btn"
              >
                {isGoogleLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting to Google...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </span>
                )}
              </Button>
            </div>
          </div>
        </form>

        {/* Feature Grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto px-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 rounded-xl bg-[#1a3a42]/80 border-2 border-[#2a9d8f]/30 hover:border-[#e9c46a]/50 transition-all duration-300 group"
              style={{ 
                boxShadow: "4px 4px 0 rgba(38, 70, 83, 0.5)",
                animationDelay: `${index * 100}ms`
              }}
            >
              <div 
                className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                style={{ boxShadow: "3px 3px 0 rgba(42, 157, 143, 0.3)" }}
              >
                <feature.icon className="w-6 h-6 text-[#e9c46a]" />
              </div>
              <h3 className="text-lg font-bold text-[#faf3e0] mb-2">{feature.title}</h3>
              <p className="text-sm text-[#a8c8c4]">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-[#2a9d8f]/30 bg-[#1a3a42]/50">
        <a 
          href="https://westbay.io" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block hover:opacity-80 transition-opacity mb-4"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_530e9795-c215-4048-be9d-12bfadc67a65/artifacts/ewtqrs16_poweredby.png" 
            alt="Powered by Poser AI"
            className="h-10 w-auto mx-auto"
          />
        </a>
        <p className="text-[#a8c8c4]/70 text-xs mb-2">
          © 2026 Westbay Group LLC. All rights reserved.
        </p>
        <p className="text-[#a8c8c4]/50 text-xs font-mono">
          Poser AI<sup>™</sup> is a trademark of Westbay Group LLC
        </p>
        <div className="flex justify-center gap-2 mt-3">
          <span className="px-2 py-1 text-xs rounded bg-[#2a9d8f]/30 text-[#40c9ba] font-mono">v1.0</span>
          <span className="px-2 py-1 text-xs rounded bg-[#e9c46a]/20 text-[#e9c46a] font-mono">BETA</span>
        </div>
      </footer>
    </div>
  );
}
